import {
  useAccess,
  useAccessRoles,
  useCampuses,
  useMembers,
  useRemoveMember,
  useSession,
  useSetMemberCampuses,
} from '@church/data'
import { faChevronDown, faCrown, faLocationDot, faUserMinus, faUserShield } from '@church/ui-icons'
import { createFileRoute } from '@tanstack/react-router'
import { ListCard, ListRow, ListSection, usePanel } from '../components/platform/SettingsList.tsx'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  EmptyState,
  ErrorNote,
  Icon,
  PanelSection,
  Select,
  SidePanel,
  Spinner,
} from '../components/ui.tsx'
import { DisplayOptions } from '../components/DisplayOptions.tsx'
import { useListDisplay } from '../lib/display.ts'

/**
 * Staff and their roles. The list says who has which role at a glance;
 * changing a role, limiting someone to a campus or removing them happens in
 * their panel — one shape on every screen size.
 */
export const Route = createFileRoute('/_authed/_shell/settings/staff')({
  component: StaffTab,
})

/** What a member's row shows on a phone; every field shows from `sm` up. */
const STAFF_FIELDS = [
  { id: 'email', label: 'Email', default: true },
  { id: 'role', label: 'Role', default: true },
  { id: 'campus', label: 'Campus scope', default: false },
] as const

function StaffTab() {
  const { members, isLoading, hasNextPage, fetchNextPage } = useMembers()
  // Presets (and custom roles made before templates, kept by whoever holds one);
  // assignment goes through our endpoint, which also enforces seats and "you
  // cannot grant what you do not hold".
  const { roles: accessRoles, assign, choicesFor } = useAccessRoles()
  const roles = accessRoles.data
  const { data: campuses } = useCampuses()
  const { me } = useSession()
  const access = useAccess()
  const setCampuses = useSetMemberCampuses()
  const remove = useRemoveMember()
  const panel = usePanel<string>()
  const display = useListDisplay('settings-staff', STAFF_FIELDS)
  const member = members.find((m) => m.id === panel.shown)

  const roleLabel = (id: string | undefined) =>
    roles?.find((r) => r.id === id)?.label ?? id ?? 'Member'
  const campusLabel = (campusIds: string[] | null) =>
    campusIds === null
      ? null
      : campusIds.map((id) => campuses?.find((c) => c.id === id)?.name ?? 'A campus').join(', ')

  if (isLoading) return <Spinner />

  return (
    <div className="max-w-2xl">
      <ListSection
        title="Staff and members"
        icon={faUserShield}
        action={
          <DisplayOptions fields={display.fields} shown={display.shown} onChange={display.set} />
        }
      >
        {members.length === 0 ? (
          <Card>
            <EmptyState title="No members" />
          </Card>
        ) : (
          <ListCard>
            {members.map((m) => {
              const scope = campusLabel(m.campusIds)
              return (
                <ListRow
                  key={m.id}
                  title={
                    <>
                      {m.name}
                      {m.userId === me?.user.id && (
                        <span className="font-normal text-content-muted"> (you)</span>
                      )}
                    </>
                  }
                  meta={<span className={display.hide('email')}>{m.email}</span>}
                  aside={
                    <>
                      {scope && (
                        <span className={`contents ${display.hide('campus')}`}>
                          <Chip tone="warning">
                            <Icon icon={faLocationDot} className="size-3" />
                            {scope}
                          </Chip>
                        </span>
                      )}
                      <span className={`contents ${display.hide('role')}`}>
                        <Chip tone={m.roles[0] === 'owner' ? 'brand' : 'neutral'}>
                          {m.roles[0] === 'owner' && <Icon icon={faCrown} className="size-3" />}
                          {roleLabel(m.roles[0])}
                        </Chip>
                      </span>
                    </>
                  }
                  onOpen={
                    access.has('org', 'administrator', { orgWide: true })
                      ? () => panel.show(m.id)
                      : undefined
                  }
                />
              )
            })}
          </ListCard>
        )}

        {hasNextPage && (
          <Button variant="secondary" icon={faChevronDown} onClick={() => void fetchNextPage()}>
            Load more
          </Button>
        )}
      </ListSection>

      <SidePanel
        open={panel.open && member !== undefined}
        onOpenChange={panel.onOpenChange}
        title={member?.name ?? ''}
        description={member?.email}
        footer={
          member && member.userId !== me?.user.id && access.managesMembership ? (
            <ConfirmButton
              label="Remove from this church"
              icon={faUserMinus}
              variant="quietDanger"
              className="-ml-4"
              disabled={remove.isPending}
              title={`Remove ${member.name}?`}
              description="They lose access to this organization immediately."
              confirmLabel="Remove person"
              onConfirm={() => remove.mutate(member.id, { onSuccess: panel.close })}
            />
          ) : undefined
        }
      >
        {member && (
          <>
            <PanelSection
              title="Role"
              hint={
                member.userId === me?.user.id
                  ? 'You can’t change your own role — someone must hold the keys.'
                  : 'You can only give a role whose permissions you hold yourself.'
              }
            >
              <Select
                aria-label={`Role for ${member.name}`}
                value={member.roles[0] ?? 'member'}
                // Owners can't demote themselves — someone must hold the keys.
                disabled={member.userId === me?.user.id || assign.isPending}
                onChange={(e) => assign.mutate({ memberId: member.id, roles: [e.target.value] })}
              >
                {choicesFor(member.roles).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </Select>
              {roles?.find((r) => r.id === member.roles[0])?.description && (
                <p className="rounded-lg bg-surface-sunken px-3 py-2 text-sm text-content-muted">
                  {roles.find((r) => r.id === member.roles[0])?.description}
                </p>
              )}
            </PanelSection>

            <PanelSection
              title="Campus scope"
              hint={
                campuses?.length
                  ? 'Limit what they see to one campus, or leave it church-wide.'
                  : 'Add campuses (the Campuses tab) to limit someone to one.'
              }
            >
              <Select
                aria-label={`Campus scope for ${member.name}`}
                value={member.campusIds === null ? '' : (member.campusIds[0] ?? '')}
                disabled={setCampuses.isPending || !campuses?.length}
                onChange={(e) =>
                  setCampuses.mutate({
                    memberId: member.id,
                    campusIds: e.target.value === '' ? null : [e.target.value],
                  })
                }
              >
                <option value="">All campuses</option>
                {(campuses ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    Only {c.name}
                  </option>
                ))}
              </Select>
            </PanelSection>

            <ErrorNote error={assign.error ?? setCampuses.error ?? remove.error} />
          </>
        )}
      </SidePanel>
    </div>
  )
}
